import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import ZoomImage from "../components/ZoomImage";
import AuthRequiredModal from "../components/AuthRequiredModal";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { Minus, Plus, Heart, Sparkles, Clock } from "lucide-react";
import { imageUrl } from "../lib/cloudinary";
import { toast } from "sonner";
import { trackViewContent, trackAddToCart } from "../lib/metaPixel";
import { pushViewItem, pushAddToCart as gtmAddToCart } from "../lib/gtmDataLayer";
import {
  SITE_ORIGIN,
  CATEGORY_TO_PATH,
  PATH_TO_CATEGORY,
  categoryPathFor,
  buildProductJsonLd,
  buildBreadcrumbJsonLd,
  productUrl,
  truncate,
} from "../lib/seo";

const BLANK_PLACEHOLDER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=";

/** Best-effort PDP rich description bullets derived from category + name.
 *  We don't have per-product editorial copy in the DB yet, so we build
 *  sensible defaults that follow Google's "informative product detail"
 *  guidance: ingredients, taste, aroma, texture, shelf life, storage,
 *  usage, net weight and the brand promise.  This block ships on the
 *  rendered HTML so Googlebot picks it up. */
function buildDescriptionBlocks({ product, seo, selectedVariant }) {
  const cat = product.category || "";
  const isPickle = cat === "Pickles";
  const isOil = cat === "Wood Pressed Oils";
  const isPaste = cat === "Culinary Pastes";
  const isMasala = cat === "Masalas" || cat === "Spices";
  const isPodi = cat === "Traditional Powders";
  const isFlour = cat === "Flours";

  const weight = selectedVariant?.label || (product.variants && product.variants[0]?.label) || "—";

  const blocks = [];
  // 1) Ingredients
  if (isPickle) blocks.push({ title: "Ingredients", body: "Hand-selected raw produce, mustard, fenugreek, Guntur red chilli, salt, turmeric and gingelly oil. No synthetic preservatives, no artificial colour." });
  else if (isOil) blocks.push({ title: "Ingredients", body: "100% pure seed — no blending, no chemicals, no refining. Traditional chekku (wood-pressed) extraction at low temperature preserves natural aroma and nutrients." });
  else if (isPaste) blocks.push({ title: "Ingredients", body: "Fresh produce ground daily with edible oil and salt. No preservatives or artificial colour — refrigerate after opening." });
  else if (isMasala || isPodi) blocks.push({ title: "Ingredients", body: "Whole spices sun-dried and stone-ground in small batches for maximum aroma. Single-source, no fillers or additives." });
  else if (isFlour) blocks.push({ title: "Ingredients", body: "Stone-ground whole grain — nothing added, nothing taken away. Naturally retains its bran, germ and nutrient profile." });

  // 2) Taste / aroma / texture
  if (isPickle) blocks.push({ title: "Taste · Aroma · Texture", body: "Bold and tangy with a deep Andhra heat; aromatic mustard-fenugreek tempering and a robust, chunky texture suspended in fragrant gingelly oil." });
  else if (isOil) blocks.push({ title: "Taste · Aroma · Texture", body: "Light golden colour, nutty fragrance and a clean, full-bodied taste. Smoke point suited for daily tempering and shallow frying." });
  else if (isPaste) blocks.push({ title: "Taste · Aroma · Texture", body: "Fresh, sharp aroma and smooth, spreadable texture — adds depth and a hand-pounded flavour your blender can't replicate." });
  else if (isMasala || isPodi) blocks.push({ title: "Taste · Aroma · Texture", body: "Roasted, aromatic and finely ground for even dispersion. Toasted-spice fragrance hits the second you open the pack." });
  else if (isFlour) blocks.push({ title: "Taste · Aroma · Texture", body: "Clean, mildly nutty aroma. Fine to medium grind suitable for porridges, dosa batter, baking and ladoos." });

  // 3) Net weight + storage + shelf life (universal)
  blocks.push({
    title: "Net weight · Shelf life · Storage",
    body: `Pack size: ${weight}. Shelf life: 6–12 months when stored in a cool, dry place — use a clean, dry spoon every time. After opening${isPickle || isPaste ? " refrigerate" : " seal tightly"} for best freshness.`,
  });

  // 4) Usage suggestions
  if (isPickle) blocks.push({ title: "How to use", body: "Pairs beautifully with hot rice and ghee, curd rice, dosa, idli, paratha and pesarattu. A teaspoon transforms a simple meal." });
  else if (isOil) blocks.push({ title: "How to use", body: "Daily cooking, tempering (talimpu), pickle making, baby massage and traditional uses. Two tablespoons replace refined oil in any home recipe." });
  else if (isPaste) blocks.push({ title: "How to use", body: "Use directly in curries, biryanis, marinades and stir-fries. Saves the prep step without compromising on a freshly-ground taste." });
  else if (isMasala || isPodi) blocks.push({ title: "How to use", body: "Sprinkle on hot rice with ghee, stir into curries, or use as a finishing spice on dosa, vada, and even popcorn." });
  else if (isFlour) blocks.push({ title: "How to use", body: "Make ragi mudde, dosa, ambali, malt, baby porridge, healthy ladoos and gluten-free baking — versatile across breakfast, mains and snacks." });

  // 5) Why Sri Durga
  blocks.push({
    title: "Why Sri Durga Foods",
    body: "Over 25 years of Andhra culinary heritage. Small-batch production, traditional methods and the same recipes we cook at home. Direct from our family kitchen in Nellore to your table.",
  });

  // 6) SEO keyword paragraph — only renders when we have a primary keyword
  if (seo?.primary_keyword) {
    const kws = (seo.supporting_keywords || []).join(", ");
    blocks.push({
      title: "Also searched as",
      body: `${seo.primary_keyword}${kws ? ` · ${kws}` : ""}.`,
    });
  }
  return blocks;
}

export default function ProductDetail() {
  // Supports BOTH /:category/:slug (new canonical) and /product/:id (legacy).
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [seo, setSeo] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const { formatThreshold } = useSettings();

  // Resolve the product — by slug if we have category+slug, else by id.
  useEffect(() => {
    let cancelled = false;
    const onArrive = (data, seoData) => {
      if (cancelled) return;
      setP(data);
      setSeo(seoData || null);
      const vs = data.variants || [];
      const sorted = [...vs].sort((a, b) => a.price - b.price);
      const initialVariant = sorted[0] || null;
      setVariant(initialVariant);
      setActiveImg(0);
      trackViewContent(data, initialVariant);
      pushViewItem(data, initialVariant);
    };

    if (params.slug && params.category) {
      api.get(`/products/by-slug/${params.category}/${params.slug}`)
        .then((r) => onArrive(r.data.product, r.data.seo))
        .catch(() => navigate("/shop"));
    } else if (params.id) {
      // Legacy /product/:id — load product, then in-place rewrite the
      // browser URL to the canonical slug so the user (and shared links
      // / refreshes) consolidate on the SEO-friendly URL.
      api.get(`/products/${params.id}`)
        .then(async (r) => {
          let seoData = null;
          try {
            const sr = await api.get(`/products/${params.id}/slug`);
            seoData = sr.data?.seo || null;
          } catch (_) { /* ignore */ }
          onArrive(r.data, seoData);
          if (seoData?.url_path && location.pathname !== seoData.url_path) {
            navigate(seoData.url_path, { replace: true });
          }
        })
        .catch(() => navigate("/shop"));
    }
    return () => { cancelled = true; };
  }, [params.id, params.category, params.slug, navigate, location.pathname]);

  // Related products — same category, exclude self.
  useEffect(() => {
    if (!p) return;
    api.get("/products", { params: { category: p.category } }).then((r) => {
      setRelated(r.data.filter((x) => x.product_id !== p.product_id).slice(0, 4));
    });
  }, [p]);

  const gallery = useMemo(() => {
    if (!p) return [];
    if (variant?.image_url) {
      const extra = (p.images || []).filter((u) => u && u !== variant.image_url);
      return [variant.image_url, ...extra];
    }
    // iter70 — for products without variants (e.g. Combos), fall back to
    // the product's main image_url + additional images gallery so admins
    // who upload combo photos see them on the PDP.
    if (p.image_url) {
      const extra = (p.images || []).filter((u) => u && u !== p.image_url);
      return [p.image_url, ...extra];
    }
    if ((p.images || []).length > 0) return p.images.filter(Boolean);
    return [BLANK_PLACEHOLDER];
  }, [p, variant]);

  const realVariants = useMemo(() => {
    if (!p?.variants?.length) return [];
    return p.variants.slice().sort((a, b) => a.price - b.price);
  }, [p]);

  useEffect(() => { setActiveImg(0); }, [variant?.label]);

  if (!p) return <div className="text-center py-32 text-brand-muted">Loading…</div>;

  const unitPrice = variant?.price ?? p.price;
  const onAdd = () => {
    add(p, qty, variant);
    trackAddToCart(p, variant, qty);
    gtmAddToCart(p, variant, qty);
    toast.success(`${p.name} added to cart`);
  };
  const buyNow = () => {
    add(p, qty, variant);
    trackAddToCart(p, variant, qty);
    gtmAddToCart(p, variant, qty);
    navigate("/cart");
  };

  // ----- SEO computed values -----
  const canonicalPath = seo?.url_path || productUrl(p);
  const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;
  // iter81 — admin-editable SEO override on the product doc. When set,
  // wins over SEO_SLUGS meta and the auto-generated fallback so any
  // admin can craft a bespoke SERP snippet for a hero product.
  const ogTitle = p.seo_title || seo?.meta_title || `${p.name} | Sri Durga Foods`;
  const ogDescription = p.seo_description || seo?.meta_description || truncate(p.description || "", 160);
  const heroImage = variant?.image_url || p.image_url || (gallery && gallery[0]);
  // Force absolute https URL for OG / JSON-LD — relative /api/media/… won't validate
  const absoluteImage =
    heroImage && heroImage.startsWith("http")
      ? heroImage
      : heroImage && heroImage.startsWith("/")
        ? `${SITE_ORIGIN}${heroImage}`
        : `${SITE_ORIGIN}/logo.png`;
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Shop", path: "/shop" },
    { name: p.category, path: `/${categoryPathFor(p.category)}` },
    { name: p.name, path: canonicalPath },
  ];
  const productJsonLd = buildProductJsonLd({
    product: p,
    seo,
    selectedVariant: variant,
    absoluteUrl: canonicalUrl,
    absoluteImage,
  });
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(breadcrumbs);
  const descriptionBlocks = buildDescriptionBlocks({ product: p, seo, selectedVariant: variant });

  // Category-aware "related" link text (uses SEO friendly URLs)
  const categoryHref = `/${categoryPathFor(p.category)}`;

  return (
    <div className="container-x py-12 lg:py-20 animate-fade-in" data-testid="product-detail">
      <SEOHead
        title={ogTitle}
        description={ogDescription}
        canonical={canonicalPath}
        image={absoluteImage}
        ogType="product"
      >
        <meta property="og:site_name" content="Sri Durga Foods" />
        <meta property="product:brand" content="Sri Durga Foods" />
        <meta property="product:availability" content={p.in_stock !== false ? "in stock" : "out of stock"} />
        <meta property="product:price:amount" content={Number(unitPrice).toFixed(2)} />
        <meta property="product:price:currency" content="INR" />
        {/* JSON-LD: Product + BreadcrumbList so Google can render rich snippets. */}
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </SEOHead>

      {/* Breadcrumb (visible) */}
      <nav aria-label="Breadcrumb" className="text-xs text-brand-muted uppercase tracking-[0.2em] mb-8" data-testid="pdp-breadcrumb">
        {breadcrumbs.map((c, i) => (
          <span key={c.path + i}>
            {i > 0 && <span className="mx-2">·</span>}
            {i < breadcrumbs.length - 1 ? (
              <Link to={c.path} className="hover:text-brand-chili">{c.name}</Link>
            ) : (
              <span className="text-brand-dark">{c.name}</span>
            )}
          </span>
        ))}
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <ZoomImage
            src={imageUrl(gallery[activeImg], { width: 900, height: 900, crop: "fill" })}
            alt={`${p.name}${variant ? ` (${variant.label})` : ""} — Sri Durga Foods`}
            className="aspect-square"
          />
          <p
            data-testid="pdp-disclaimer"
            className="mt-3 text-right leading-snug line-clamp-2"
            style={{ color: "#E11D24", fontSize: "9pt" }}
          >
            Disclaimer: Product images are for illustrative purposes only. Actual product appearance may vary slightly.
          </p>
          {gallery.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
              {gallery.map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-lg border-2 overflow-hidden flex-shrink-0 transition-all ${activeImg === i ? "border-brand-chili" : "border-brand-line hover:border-brand-chili/40"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img
                    src={imageUrl(src, { width: 120, height: 120, crop: "fill" })}
                    alt={`${p.name} thumbnail ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <span className="overline mb-3 block">
            <Link to={categoryHref} className="hover:text-brand-chili" data-testid="pdp-category-link">{p.category}</Link>
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark leading-tight mb-4" data-testid="product-name">{p.name}</h1>
          {/* iter68 — MRP struck / selling price highlighted / You Save ₹X.
               Only renders when `p.mrp` is set AND greater than the current
               selling price (mandatory for Combos, unused elsewhere). */}
          {p.mrp && p.mrp > unitPrice ? (
            <div className="mb-6" data-testid="pdp-price-block">
              <div className="text-sm text-brand-muted line-through leading-none" data-testid="pdp-mrp">
                MRP ₹{Number(p.mrp).toFixed(0)}
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="font-serif text-3xl text-brand-chili font-semibold" data-testid="product-detail-price">₹{unitPrice.toFixed(0)}</span>
                {variant && <span className="text-sm text-brand-muted">/ {variant.label}</span>}
                {p.is_bestseller && <span className="badge-bestseller ml-2">Bestseller</span>}
              </div>
              <div className="mt-1 text-sm font-medium text-brand-success" data-testid="pdp-savings">
                You Save ₹{Number(p.mrp - unitPrice).toFixed(0)}
              </div>
            </div>
          ) : (
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-serif text-3xl text-brand-chili font-semibold" data-testid="product-detail-price">₹{unitPrice.toFixed(0)}</span>
              {variant && <span className="text-sm text-brand-muted">/ {variant.label}</span>}
              {p.is_bestseller && <span className="badge-bestseller ml-2">Bestseller</span>}
            </div>
          )}
          <p className="text-base text-brand-muted leading-relaxed mb-8">{p.description}</p>

          {/* Variant selector */}
          {realVariants.length > 0 && (
            <div className="mb-6">
              <div className="label-field">Size</div>
              <div className="flex flex-wrap gap-2" data-testid="variant-selector">
                {realVariants.map((v) => (
                  <button
                    key={v.label}
                    onClick={() => setVariant(v)}
                    disabled={!v.available}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${
                      variant?.label === v.label
                        ? "bg-brand-dark text-white border-brand-dark"
                        : "bg-white text-brand-dark border-brand-line hover:border-brand-chili"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid={`variant-${v.label.replace(/\s+/g, "-")}`}
                  >
                    {v.label} <span className="opacity-70 ml-1">₹{v.price.toFixed(0)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {p.in_stock ? (
            <>
              <div className="label-field">Quantity</div>
              <div className="inline-flex items-center border border-brand-line rounded-full overflow-hidden mb-6">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-brand-subtle" data-testid="qty-decrement"><Minus className="w-4 h-4" strokeWidth={1.5} /></button>
                <span className="px-6 text-base font-medium" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-brand-subtle" data-testid="qty-increment"><Plus className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>

              <div className="flex flex-wrap gap-3 mb-10">
                <button onClick={onAdd} className="btn-outline" data-testid="add-to-cart-btn">Add to cart</button>
                <button onClick={buyNow} className="btn-primary" data-testid="buy-now-btn">Buy now · ₹{(unitPrice * qty).toFixed(0)}</button>
              </div>
            </>
          ) : (
            <div className="bg-brand-subtle border border-brand-line text-brand-muted px-5 py-4 rounded-xl mb-10 text-sm" data-testid="out-of-stock-msg">
              Currently out of stock — back soon.
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 border-t border-brand-line pt-8" data-testid="pdp-claims">
            <div>
              <Heart className="w-6 h-6 text-brand-chili mb-2" strokeWidth={1.3} />
              <div className="text-xs font-bold uppercase tracking-wider mb-1" data-testid="pdp-claim-homemade">Homemade taste</div>
              <div className="text-xs text-brand-muted">Prepared using time-tested methods.</div>
            </div>
            <div>
              <Sparkles className="w-6 h-6 text-brand-chili mb-2" strokeWidth={1.3} />
              <div className="text-xs font-bold uppercase tracking-wider mb-1" data-testid="pdp-claim-authentic">Authentic Andhra flavours with quality assurance</div>
              <div className="text-xs text-brand-muted">Bold, traditional regional taste. Made under strict quality standards.</div>
            </div>
            <div>
              <Clock className="w-6 h-6 text-brand-chili mb-2" strokeWidth={1.3} />
              <div className="text-xs font-bold uppercase tracking-wider mb-1" data-testid="pdp-claim-25years">Over 25 years of trust</div>
              <div className="text-xs text-brand-muted">Serving traditional flavours for over 25 years.</div>
            </div>
          </div>
        </div>
      </div>

      {/* iter70 — Combos: "Products Included" breakdown */}
      {p.category === "Combos" && Array.isArray(p.combo_items_resolved) && p.combo_items_resolved.length > 0 && (
        <section className="mt-16 lg:mt-20 max-w-3xl" data-testid="pdp-combo-items">
          <h2 className="font-serif text-2xl sm:text-3xl text-brand-dark mb-6">Products Included</h2>
          <ul className="divide-y divide-brand-line border-y border-brand-line">
            {p.combo_items_resolved.map((ci, i) => (
              <li key={`${ci.product_id}-${ci.variant_label}-${i}`} className="flex items-center gap-4 py-4" data-testid={`combo-item-${i}`}>
                {/* iter75 — per-item image removed. The combo already has a
                    single hero image showing every included product, so the
                    small placeholder / thumbnail here was redundant. */}
                <div className="flex-1 min-w-0">
                  <div className="text-brand-dark font-medium" data-testid={`combo-item-name-${i}`}>{ci.name}</div>
                  <div className="text-xs text-brand-muted mt-0.5">
                    {ci.variant_label}{ci.quantity > 1 ? ` · Qty ${ci.quantity}` : ""}
                  </div>
                </div>
                <div className="text-sm font-medium text-brand-dark" data-testid={`combo-item-mrp-${i}`}>
                  ₹{Number(ci.individual_mrp).toFixed(0)}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Rich description blocks (SEO-indexable) */}
      <section className="mt-16 lg:mt-20 max-w-3xl" data-testid="pdp-rich-description">
        <h2 className="font-serif text-2xl sm:text-3xl text-brand-dark mb-6">About this {p.category.toLowerCase()}</h2>
        <div className="space-y-5">
          {descriptionBlocks.map((b) => (
            <div key={b.title}>
              <h3 className="text-sm uppercase tracking-wider text-brand-chili mb-1">{b.title}</h3>
              <p className="text-brand-muted leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-24 lg:mt-32">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-dark">You may also love</h2>
            <Link to={categoryHref} className="text-sm text-brand-chili border-b border-brand-chili pb-0.5" data-testid="pdp-browse-category">Browse {p.category} →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-7">
            {related.map((r, i) => <ProductCard key={r.product_id} p={r} index={i} />)}
          </div>
        </section>
      )}
      <AuthRequiredModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </div>
  );
}
